/**
 * Admin Authentication Service
 * Handles authentication specifically for admin portal with cookie-based auth
 */
export const adminAuthService = {
  /**
   * Login admin user
   */
  async login(email, password) {
    const response = await fetch('/api/admin/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // IMPORTANT: Send/receive cookies
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Invalid credentials');
    }

    const data = await response.json();

    const user = data;
    // Verify this is actually an admin/platform staff
    if (!user || !['PLATFORM_OWNER', 'PLATFORM_ADMIN'].includes(user.platformRole)) {
      throw new Error('Access denied. Platform staff only.');
    }

    // Store admin user data (NOT tokens - they're in HTTP-only cookies)
    localStorage.setItem('adminUser', JSON.stringify({
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      platformRole: user.platformRole,
      permissions: user.permissions || []
    }));
    
    return data;
  },

  /**
   * Logout admin user
   */
  async logout() {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include', // Send cookies to clear them
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear localStorage
    localStorage.removeItem('adminUser');
    
    // Redirect to admin login
    window.location.href = '/admin/login';
  },

  /**
   * Get stored admin user data
   */
  getAdminUser() {
    const user = localStorage.getItem('adminUser');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    const user = this.getAdminUser();
    return !!user && ['PLATFORM_OWNER', 'PLATFORM_ADMIN'].includes(user.platformRole);
  },

  /**
   * Make authenticated API request
   * Cookies are automatically sent with credentials: 'include'
   */
  async fetch(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      credentials: 'include', // IMPORTANT: Send cookies automatically
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      }
    });

    if (response.status === 401) {
      this.logout();
      throw new Error('Session expired');
    }

    return response;
  }
};

export default adminAuthService;
